import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError, timer } from 'rxjs';

import { environment } from '../../../environments/environment';
import { SessionRefreshService } from '../services/auth/session-refresh.service';
import { SKIP_AUTH_REFRESH } from './auth-refresh-context';

let redirectingToLogin = false;

export const authExpiredInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionRefresh = inject(SessionRefreshService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!shouldAttemptRefresh(req, error)) {
        return throwError(() => error);
      }

      return sessionRefresh.refresh().pipe(
        switchMap(() => timer(250)),
        switchMap(() => next(withCredentialedFetch(req))),
        catchError((refreshError: unknown) => {
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};

function withCredentialedFetch(req: HttpRequest<unknown>): HttpRequest<unknown> {
  return req.credentials === 'include'
    ? req
    : req.clone({ withCredentials: false, credentials: 'include' });
}

function shouldAttemptRefresh(req: HttpRequest<unknown>, error: unknown): boolean {
    if (req.headers.has('X-Financial-Confirmation')) return false;
  return typeof window !== 'undefined'
    && !req.context.get(SKIP_AUTH_REFRESH)
    && isAuthExpired(error)
    && !isAuthEndpoint(req);
}

function isAuthEndpoint(req: HttpRequest<unknown>): boolean {
  const url = req.url;
  return (
    url.includes('/api/v1/user/login') ||
    url.includes('/api/v1/user/pre-authenticate') ||
    url.includes('/api/v1/user/refresh') ||
    url.includes('/api/v1/user/logout')
  );
}

export function isAuthExpired(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }

  const errorCode = String(error.error?.error ?? error.error?.errorCode ?? '').toUpperCase();
  const message = String(error.error?.message ?? '').toLowerCase();
  const url = String(error.url ?? '').toLowerCase();
  const authEndpoint = url.includes('/api/v1/user/');

  return (
    error.status === 401 ||
    errorCode === 'AUTHORIZATION_FAILED' ||
    (error.status === 403 && authEndpoint && message.includes('authorization failed'))
  );
}

export function redirectToLogin(): void {
  if (redirectingToLogin || typeof window === 'undefined') {
    return;
  }

  redirectingToLogin = true;
  const loginUrl = new URL('/login', environment.publicAppUrl);
  loginUrl.searchParams.set('returnUrl', window.location.href);
  appendLoginStatusParams(loginUrl);
  window.location.href = loginUrl.toString();
}

function appendLoginStatusParams(loginUrl: URL): void {
  const currentUrl = new URL(window.location.href);
  for (const key of ['passwordCreated', 'accountSetup', 'reset']) {
    const value = currentUrl.searchParams.get(key)?.trim();
    if (value === 'success') {
      loginUrl.searchParams.set(key, value);
    }
  }
}
